/**
 * One constant, one file, shared/. All durations in the sim are expressed in
 * ticks, never milliseconds. Changing TICK_MS is a wipe-level migration —
 * it invalidates every replay, leaderboard entry, and scenario fixture.
 *
 * Useful conversion: 20 ticks = 1 second.
 *
 * TUNABLE NUMBERS NO LONGER LIVE HERE: the combat + movement knobs moved to
 * `combat.store` (the single source of truth, `npm run knobs` → Combat tab).
 * `shared/combat.ts` carries their committed default (`COMBAT`) and the
 * per-run table rides `State.combat`, so a dev-server knob edit is live on
 * the next floor with no rebuild (docs/design/knobs.md). This file keeps
 * only RULES: sentinels, ids, and the two swing-tick helpers.
 */
export const TICK_MS = 50;

/** Largest party a door accepts (docs/game.md). The boards' party axis runs
 *  1..MAX_PARTY_SIZE, and a board's cutoff is a party size in that range. */
export const MAX_PARTY_SIZE = 5;

/** Sentinel target id for interacting with the boss room (floor goal). */
export const BOSS_TARGET_ID = -1;

/** Sentinel target id for the return portal (the base room's way back to the
 *  hub — it ends YOUR floor, not the floor's). The exit portal is BOSS_TARGET_ID;
 *  the return portal is a different object doing a different job (docs/design/run.md). */
export const RETURN_PORTAL_TARGET_ID = -2;

/**
 * The exit countdown, in ticks, PER PLAYER WHO HAS NOT ACCEPTED (docs/design/
 *  run.md). 1200 ticks = 60 s at 20 tps (TICK_MS). The countdown starts when
 *  the first non-host player accepts and only ever shrinks: each acceptance
 *  buys back exactly one minute ("the last minute belongs to whoever hasn't
 *  shown up — including a host who isn't coming back"). The host's interact
 *  ends the floor instantly (no countdown); solo, you are the host.
 */
export const EXIT_COUNTDOWN_TICKS_PER_UNACCEPTED = 1200;

/**
 * Stone id of the Group Gatestone. Personal gatestones use the stable id
 * `playerId + 1` (one owned per player, fixed identity across generate/place/
 * teleport — see sim/step.ts). Ids are deterministic; no counter state.
 */
export const GROUP_STONE_ID = 0;

// ---- combat (M4) ---------------------------------------------------------
// A swing is a duration in whole ticks; the hit resolves at the impact tick
// (a fixed fraction through the swing) against CURRENT positions — hits are
// authoritative, not predicted. Speeds are AUTHORED IN TICKS (ItemStats /
// NpcDef `speedTicks`) — the sim never deals in ms or floats (TICK_MS exists
// for clients and tools that display the ms equivalent, nothing else).

/** Clamp an authored swing cadence to a safe whole-tick duration (min 1). */
export function clampSwingTicks(ticks: number): number {
  return Math.max(1, ticks);
}

/** The impact tick, as an offset from swing start (the blade is down ~75%). */
export function impactTicks(swing: number): number {
  return Math.max(1, Math.round(swing * 0.75));
}

/**
 * The two-roll combat model — THE ACCURACY CONTEST (sim/combat.ts resolveHit):
 *
 *   attack = weapon's attack + the level row's atkBonus   (hero)
 *            the NPC's attack                              (enemy)
 *   defense = the target's meleeDef / magicDef bucket for THIS attack's
 *             type (melee weapon/ability/NPC style → meleeDef; magic →
 *             magicDef; the reserved ranged folds into meleeDef — an arrow
 *             is physical). Hero bucket = SUM across armor slots.
 *   chance = hitChanceBps(attack, defense, accFloorPct, accCapPct)
 *            = floor + (cap − floor) × attack/(attack + defense)  — integer
 *              basis points (shared/combat.ts; the arena line + knobs DPS
 *              tab draw with the SAME function)
 *   hit  = rng.int(10000) < chance          — one roll to touch
 *   dmg  = roll(atkMin..max(atkMin, floor(atkMax × dmgMod / 100))) — one roll
 *
 * DEFENSE affects hit chance ONLY — a hit always lands the weapon's full
 * damage (soak was tried and rejected: it makes every authored number
 * smaller). The contest curve replaces the old percent-tax model
 * (accuracy × (100 − defense)): attack and defense are POINTS on one scale
 * (weapons ~10–100), neither can wall the other — the floor knob means the
 * weakest matchup still lands occasionally, the cap knob means nobody is
 * unhittable-proof, and the same defense point matters more against weak
 * attacks than strong ones (the interaction the tuning is built around —
 * the knobs DPS tab is the authoring surface for it). The level row's
 * atkBonus (flat points, replaces the multiplicative accMod) is the
 * class/level plug-in point (docs/design/classes.md). The base values live
 * in combat.store (shared/combat.ts `COMBAT`), read per-run from state.
 */
